import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import api from '../services/api';
import { AUTH_STORAGE_CHANGED_EVENT, REQUIRED_ACTION_PENDING_EVENT } from '../services/api';
import { addOperationalBreadcrumb, setOperationalUserContext } from '../observability/sentry';
import type { AuthBootstrapCapabilities, AuthBootstrapPayload, AuthBootstrapSubscriptionState, RequiredUserAction, Tenant, User } from '../types';

const SWITCHED_TENANT_ID_STORAGE_KEY = 'switchedTenantId';
const SWITCHED_TENANT_NAME_STORAGE_KEY = 'switchedTenantName';

interface AuthContextType {
  user: User | null;
  bootstrap: AuthBootstrapPayload | null;
  capabilities: AuthBootstrapCapabilities | null;
  subscriptions: {
    ai: AuthBootstrapSubscriptionState;
    reverseIvr: AuthBootstrapSubscriptionState;
  } | null;
  requiredActions: RequiredUserAction[];
  token: string | null;
  sessionId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperuser: boolean;
  // Tenant switching
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  searchTenants: (search?: string, limit?: number) => Promise<Tenant[]>;
  switchTenant: (tenantId: string | null, tenantName?: string | null) => void;
  // Auth methods
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (partial: Partial<User>) => void;
  refreshBootstrap: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const TENANT_SWITCH_RETURN_TO_STORAGE_KEY = 'tenantSwitchReturnTo';

function buildTenantSnapshot(id: string, name: string): Tenant {
  return {
    id,
    name,
    email: '',
    isActive: true,
    isSystem: false,
    userCount: 0,
    createdAt: '',
    updatedAt: '',
  };
}

function buildUserSnapshot(userData: Partial<User>): User {
  return {
    id: String(userData.id || ''),
    email: String(userData.email || ''),
    firstName: String(userData.firstName || ''),
    lastName: String(userData.lastName || ''),
    isStaff: Boolean(userData.isStaff),
    isSuperuser: Boolean(userData.isSuperuser),
    isSystemAdmin: Boolean(userData.isSystemAdmin),
    tenantId: (userData.tenantId as string | null | undefined) ?? null,
    tenantName: (userData.tenantName as string | null | undefined) ?? null,
    language: (userData.language as string | undefined) ?? undefined,
    theme: (userData.theme as string | undefined) ?? undefined,
    telephonyEndpoint: (userData.telephonyEndpoint as User['telephonyEndpoint']) ?? null,
    telephonyAvailability: (userData.telephonyAvailability as User['telephonyAvailability']) ?? null,
  };
}

/**
 * Provides authentication state and tenant switching.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [bootstrap, setBootstrap] = useState<AuthBootstrapPayload | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem('sessionId'));
  const [isLoading, setIsLoading] = useState(true);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);

  const isSuperuser = user?.isSuperuser || user?.isSystemAdmin || false;
  const capabilities = bootstrap?.capabilities ?? null;
  const subscriptions = bootstrap?.subscriptions ?? null;
  const requiredActions = bootstrap?.requiredActions ?? [];

  /**
   * Search active tenants for tenant-switch UI.
   */
  const searchTenants = useCallback(async (search = '', limit = 30): Promise<Tenant[]> => {
    if (!isSuperuser) {
      setAvailableTenants([]);
      return [];
    }

    try {
      const response = await api.get('/api/auth/tenants/', {
        params: {
          search: search || undefined,
          limit,
        },
      });
      const rows = Array.isArray(response.data?.tenants) ? response.data.tenants : [];
      const nextTenants = rows.map((tenant: { id?: string; name?: string; email?: string; isActive?: boolean; isSystem?: boolean }) => ({
        id: String(tenant.id || ''),
        name: String(tenant.name || ''),
        email: String(tenant.email || ''),
        isActive: Boolean(tenant.isActive ?? true),
        isSystem: Boolean(tenant.isSystem ?? false),
        userCount: 0,
        createdAt: '',
        updatedAt: '',
      }));
      setAvailableTenants(nextTenants);
      return nextTenants;
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setAvailableTenants([]);
      return [];
    }
  }, [isSuperuser]);

  const applyBootstrap = useCallback((bootstrapData: AuthBootstrapPayload) => {
    const normalizedBootstrapData = {
      ...bootstrapData,
      requiredActions: Array.isArray(bootstrapData.requiredActions) ? bootstrapData.requiredActions : [],
    };
    const nextUser = buildUserSnapshot(normalizedBootstrapData.user);
    setBootstrap(normalizedBootstrapData);
    setUser(nextUser);

    const savedTenantId = localStorage.getItem(SWITCHED_TENANT_ID_STORAGE_KEY);
    const savedTenantName = localStorage.getItem(SWITCHED_TENANT_NAME_STORAGE_KEY);
    const isPlatformAdmin = Boolean(nextUser.isSuperuser || nextUser.isSystemAdmin);
    if (isPlatformAdmin && savedTenantId) {
      setCurrentTenant(
        buildTenantSnapshot(
          savedTenantId,
          savedTenantName || nextUser.tenantName || savedTenantId,
        ),
      );
      return;
    }

    if (nextUser.tenantId && nextUser.tenantName) {
      setCurrentTenant(buildTenantSnapshot(nextUser.tenantId, nextUser.tenantName));
      return;
    }

    setCurrentTenant(null);
  }, []);

  const applyPendingRequiredAction = useCallback((requiredAction: RequiredUserAction) => {
    setBootstrap((prev) => {
      if (!prev) {
        return prev;
      }
      const existingActions = Array.isArray(prev.requiredActions) ? prev.requiredActions : [];
      const withoutCurrent = existingActions.filter((action) => action.id !== requiredAction.id);
      return {
        ...prev,
        requiredActions: [requiredAction, ...withoutCurrent],
      };
    });
  }, []);

  const refreshBootstrap = useCallback(async () => {
    const response = await api.get('/api/auth/bootstrap/');
    const bootstrapData = response.data as AuthBootstrapPayload;
    applyBootstrap(bootstrapData);
  }, [applyBootstrap]);

  const syncAuthFromStorage = useCallback(() => {
    const nextToken = localStorage.getItem('token');
    const nextSessionId = localStorage.getItem('sessionId');

    setToken((prev) => (prev === nextToken ? prev : nextToken));
    setSessionId((prev) => (prev === nextSessionId ? prev : nextSessionId));

    if (nextToken) {
      return;
    }

    setBootstrap(null);
    setUser(null);
    setCurrentTenant(null);
    setAvailableTenants([]);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      if (user && bootstrap) {
        setIsLoading(false);
        return;
      }

      try {
        await refreshBootstrap();
      } catch (error) {
        // Only clear auth on actual auth failures (401)
        // The api interceptor handles 401 for /api/auth/bootstrap specifically
        console.error('Auth init failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem(SWITCHED_TENANT_ID_STORAGE_KEY);
        localStorage.removeItem(SWITCHED_TENANT_NAME_STORAGE_KEY);
        localStorage.removeItem('sessionId');
        setToken(null);
        setSessionId(null);
        setBootstrap(null);
        setUser(null);
        setCurrentTenant(null);
      }
      setIsLoading(false);
    };
    initAuth();
  }, [bootstrap, refreshBootstrap, token, user]);

  useEffect(() => {
    const handleAuthStorageChanged = () => {
      syncAuthFromStorage();
    };
    const handleStorage = (event: StorageEvent) => {
      if (
        event.key
        && event.key !== 'token'
        && event.key !== 'sessionId'
        && event.key !== 'refreshToken'
      ) {
        return;
      }
      syncAuthFromStorage();
    };

    window.addEventListener(AUTH_STORAGE_CHANGED_EVENT, handleAuthStorageChanged as EventListener);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(AUTH_STORAGE_CHANGED_EVENT, handleAuthStorageChanged as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [syncAuthFromStorage]);

  useEffect(() => {
    const handleRequiredActionPending = (event: Event) => {
      const detail = (event as CustomEvent<{ requiredAction?: RequiredUserAction }>).detail;
      const requiredAction = detail?.requiredAction;
      if (!requiredAction?.id) {
        return;
      }
      applyPendingRequiredAction(requiredAction);
    };

    window.addEventListener(REQUIRED_ACTION_PENDING_EVENT, handleRequiredActionPending as EventListener);
    return () => {
      window.removeEventListener(REQUIRED_ACTION_PENDING_EVENT, handleRequiredActionPending as EventListener);
    };
  }, [applyPendingRequiredAction]);

  useEffect(() => {
    setOperationalUserContext({
      user,
      currentTenant,
      isAuthenticated: Boolean(user),
    });
  }, [currentTenant, user]);

  // Update current tenant when availableTenants changes and we have a saved tenant
  useEffect(() => {
    const savedTenantId = localStorage.getItem(SWITCHED_TENANT_ID_STORAGE_KEY);
    if (!savedTenantId || availableTenants.length === 0) {
      return;
    }
    const tenant = availableTenants.find((row) => row.id === savedTenantId);
    if (!tenant) {
      return;
    }
    setCurrentTenant(tenant);
    localStorage.setItem(SWITCHED_TENANT_NAME_STORAGE_KEY, tenant.name);
  }, [availableTenants]);

  const switchTenant = useCallback((tenantId: string | null, tenantName?: string | null) => {
    const normalizedTenantId = tenantId ? String(tenantId).trim() : null;
    if (!normalizedTenantId) {
      return;
    }
    if (normalizedTenantId === currentTenant?.id) {
      return;
    }

    const tenantFromCache = availableTenants.find((row) => row.id === normalizedTenantId);
    const resolvedName = tenantFromCache?.name || String(tenantName || '').trim() || normalizedTenantId;
    const nextTenant = tenantFromCache || buildTenantSnapshot(normalizedTenantId, resolvedName);
    addOperationalBreadcrumb({
      category: 'auth.tenant',
      message: 'Tenant switch requested',
      level: 'info',
      data: {
        nextTenantId: normalizedTenantId,
        nextTenantName: resolvedName,
        currentTenantId: currentTenant?.id || null,
      },
    });
    setCurrentTenant(nextTenant);
    localStorage.setItem(SWITCHED_TENANT_ID_STORAGE_KEY, normalizedTenantId);
    localStorage.setItem(SWITCHED_TENANT_NAME_STORAGE_KEY, resolvedName);

    // Persist current route so bootstrap can restore it if any guard redirects.
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    localStorage.setItem(TENANT_SWITCH_RETURN_TO_STORAGE_KEY, returnTo);
    window.location.assign(returnTo);
  }, [availableTenants, currentTenant?.id]);

  const clearLocalAuthState = useCallback(() => {
    addOperationalBreadcrumb({
      category: 'auth',
      message: 'Local auth state cleared',
      level: 'info',
      data: {
        hadToken: Boolean(localStorage.getItem('token')),
        hadSessionId: Boolean(localStorage.getItem('sessionId')),
      },
    });
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(SWITCHED_TENANT_ID_STORAGE_KEY);
    localStorage.removeItem(SWITCHED_TENANT_NAME_STORAGE_KEY);
    localStorage.removeItem(TENANT_SWITCH_RETURN_TO_STORAGE_KEY);
    localStorage.removeItem('sessionId');
    setToken(null);
    setSessionId(null);
    setUser(null);
    setBootstrap(null);
    setCurrentTenant(null);
    setAvailableTenants([]);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.post('/api/auth/login/', { email, password });
    addOperationalBreadcrumb({
      category: 'auth',
      message: 'Login succeeded',
      level: 'info',
      data: {
        emailDomain: String(email || '').split('@')[1] || null,
      },
    });
    // Data is already transformed to camelCase by the API interceptor
    const { token: newToken, user: userData } = response.data;

    localStorage.setItem('token', newToken);
    if (response.data.refresh) {
      localStorage.setItem('refreshToken', response.data.refresh);
    } else {
      localStorage.removeItem('refreshToken');
    }
    localStorage.removeItem(SWITCHED_TENANT_ID_STORAGE_KEY);
    localStorage.removeItem(SWITCHED_TENANT_NAME_STORAGE_KEY);
    localStorage.removeItem(TENANT_SWITCH_RETURN_TO_STORAGE_KEY);
    if (response.data.sessionId) {
      localStorage.setItem('sessionId', response.data.sessionId);
      setSessionId(response.data.sessionId);
    } else {
      localStorage.removeItem('sessionId');
      setSessionId(null);
    }

    setIsLoading(true);
    setToken(newToken);
    setBootstrap(null);
    setUser(buildUserSnapshot(userData));

    if (userData.tenantId && userData.tenantName) {
      setCurrentTenant(buildTenantSnapshot(userData.tenantId, userData.tenantName));
    } else {
      setCurrentTenant(null);
    }
  };

  const logout = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      try {
        await api.post('/api/auth/logout/');
      } catch (error) {
        console.warn('Backend logout failed; clearing local auth state anyway.', error);
      }
    }
    addOperationalBreadcrumb({
      category: 'auth',
      message: 'Logout completed',
      level: 'info',
      data: {
        hadToken: Boolean(currentToken),
      },
    });
    clearLocalAuthState();
  }, [clearLocalAuthState]);

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((prev) => (prev ? ({ ...prev, ...partial } as User) : prev));
    setBootstrap((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        user: {
          ...prev.user,
          ...partial,
        },
      };
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      bootstrap,
      capabilities,
      subscriptions,
      requiredActions,
      token,
      sessionId,
      isAuthenticated: !!user,
      isLoading,
      isSuperuser,
      currentTenant,
      availableTenants,
      searchTenants,
      switchTenant,
      login,
      logout,
      updateUser,
      refreshBootstrap,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Access authentication state and actions.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
